// Helper functions
const isBrowser: () => boolean = (): boolean => typeof window !== 'undefined';

/**
 * Gets the user credentials from local storage.
 * This function is mean to be used inside a wrapper function for each of the current projects.
 *
 * @param key This is the key for localStorage. Ex "userCredentials", "userAuth", etc
 * @template T This is the expected UserCredential type
 * @returns {Partial<T>}
 */
export const getUserCredentials: <T>(key: string) => Partial<T> = (key) => {
  if(isBrowser()) {
    return window.localStorage.getItem(key)
    ? JSON.parse(window.localStorage.getItem(key) || '{}')
    : {};
  }
}

/**
 *
 * @param key This is the key for localStorage. Ex "userCredentials", "userAuth", etc
 * @template T The user credentials type
 * @param user The actual user object of credentials type. Ex {token:"", username:"john", ...}
 * @returns
 */
export const setUserCredentials: <T>(key: string, user: Partial<T>) => void = (
  key,
  user
) => isBrowser() && window.localStorage.setItem(key, JSON.stringify(user));

/**
 * An optional function that will return a true if a user is logged in
 *
 * @param key This is the key for localStorage. Ex "userCredentials", "userAuth", etc
 * @returns {boolean}
 */
export const isLoggedIn: (key: string) => boolean = (key) => {
  const user = getUserCredentials(key);

  return user != null && Object.keys(user).length > 0;
};

/**
 * The function to be called when a user logs out or when the authentication token is expired.
 * @param key This is the key for localStorage. Ex "userCredentials", "userAuth", etc
 * @param callback A callback to be invoked when credentials are reset to an empty object.
 */
export const logout: (key: string, callback: () => void) => void = (
  key,
  callback
) => {
  setUserCredentials(key, {});
  callback();
};
